import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'

type KBItem = {
  id: number
  ticket_request_id: number | null
  topic: string | null
  solution: string
  approved_at: string | null
}

export default function KnowledgeLibraryPage() {
  const colors = useThemeColors()
  const [items, setItems] = useState<KBItem[]>([])
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')

  useEffect(() => {
    apiClient.get('/knowledge-base', { params: { search } }).then((res) => {
      const payload = res.data?.data?.data ?? res.data?.data ?? []
      setItems(Array.isArray(payload) ? payload : [])
    })
  }, [search])

  const handleSearch = () => setSearch(searchInput.trim())
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch()
  }
  const emptyStateText = useMemo(() => (search ? 'No matching knowledge entries found.' : 'No approved knowledge entries yet.'), [search])

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Knowledge Solutions
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Browse approved technical solutions and reusable knowledge entries
              </Text>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      value={searchInput}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Search by topic or solution..."
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody p={0} pt={1} pb={4} mx={5} flex="1" overflow="hidden" display="flex" flexDirection="column" minH={0}>
              <TableContainer flex="1" overflowY="auto" border="1px" borderColor={colors.border} borderRadius="md">
                <Table size="sm">
                  <Thead position="sticky" top={0} zIndex={1} bg={colors.cardBg}>
                    <Tr>
                      <Th>Original Ticket ID</Th>
                      <Th>Topic</Th>
                      <Th>Approved Script / Solution</Th>
                      <Th>Date Approved</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {items.length === 0 ? (
                      <Tr>
                        <Td colSpan={4} textAlign="center" py={8} color={colors.textMuted}>
                          {emptyStateText}
                        </Td>
                      </Tr>
                    ) : (
                      items.map((item) => (
                        <Tr key={item.id}>
                          <Td>{item.ticket_request_id ?? '—'}</Td>
                          <Td>{item.topic || '—'}</Td>
                          <Td whiteSpace="pre-wrap">{item.solution}</Td>
                          <Td>{item.approved_at ? new Date(item.approved_at).toLocaleString() : '—'}</Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </AdminLayout>
  )
}
